import {
  CheckOutlined,
  ClearOutlined,
  DeleteOutlined,
  EditOutlined,
  PrinterOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  PlusOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { transportApi } from "../../api/transportApi";
import { getDefaultContext } from "../../api/httpClient";
import { getApiErrorMessage } from "../../utils/apiError";
import { getEnteredByUser } from "../../utils/currentUser";
import { LookupSelect } from "../../shared/components/LookupSelect";
import { LocationAutoComplete } from "../../shared/components/LocationAutoComplete";
import { BranchCreateModal } from "../../shared/components/BranchCreateModal";
import type { LookupItem } from "../../shared/components/LookupModal";
import { SaveActionButton } from "../../shared/components/SaveActionButton";
import { usePageKeyboardShortcuts } from "../../shared/hooks/usePageKeyboardShortcuts";
import { useOperationsWorkspace } from "../../shared/components/OperationsTransactionTabs";
import { EntityAttachmentsPanel } from "../../shared/components/EntityAttachmentsPanel";

type ArrivalItem = {
  key: string;
  consignmentNo: string;
  source: string;
  noPackages: number;
  short: number;
  excess: number;
  damage: number;
  isExcess: boolean;
  isTheft: boolean;
  isSeized: boolean;
  remarks: string;
};

type ArrivalSummary = {
  id: number;
  arrivalNo: string;
  arrivalDate: string;
  challanNo: string;
  fromStation: string;
  lorryNo: string;
  pkgsDespatched: number;
  pkgsReceived: number;
  shortExcess: number;
  itemCount: number;
  branchId: number;
  branchName: string;
  isProcessed: boolean;
  processedAt?: string;
  processedBy?: string;
};

type ArrivalDetail = ArrivalSummary & {
  documentDate: string;
  challanDate: string;
  arrivedAt: string;
  unloadedBy?: string;
  godown?: string;
  amount: number;
  manifestId?: number;
  transportMode?: string;
  railwayReceiptNo?: string;
  trainNo?: string;
  wagonNo?: string;
  remarks?: string;
  enteredBy: string;
  items: Array<{
    id: number;
    consignmentNo: string;
    source: string;
    noPackages: number;
    short: number;
    excess: number;
    damage: number;
    isExcess: boolean;
    isTheft: boolean;
    isSeized: boolean;
    remarks?: string;
  }>;
};

let itemKeySeq = 1;
const newKey = () => `item-${itemKeySeq++}`;

function itemsFromDetail(detail: ArrivalDetail): ArrivalItem[] {
  return detail.items.map(i => ({
    key:           newKey(),
    consignmentNo: i.consignmentNo,
    source:        i.source,
    noPackages:    i.noPackages,
    short:         i.short,
    excess:        i.excess,
    damage:        i.damage,
    isExcess:      i.isExcess,
    isTheft:       i.isTheft,
    isSeized:      i.isSeized,
    remarks:       i.remarks ?? "",
  }));
}

const blankItem = (): ArrivalItem => ({
  key: newKey(), consignmentNo: "", source: "", noPackages: 0,
  short: 0, excess: 0, damage: 0,
  isExcess: false, isTheft: false, isSeized: false, remarks: "",
});

// ─── Consignment search cell ────────────────────────────────────────────────
// Shown in each row of the arrival item grid. Searches consignments as the user
// types and auto-populates source + package count when one is selected.
type CnmtSuggest = { value: string; label: string; id: number; origin: string; totalPackages: number };

const ConsignmentRowCell = ({
  value,
  onChange,
  onFill,
  onConsignmentSelected,
}: {
  value: string;
  onChange: (v: string) => void;
  onFill: (origin: string, packages: number) => void;
  /** Fired when user picks a suggestion — parent can use this to load the
   *  parent manifest if the arrival header fields are still empty. */
  onConsignmentSelected?: (consignmentNo: string) => void;
}) => {
  const [opts, setOpts] = useState<CnmtSuggest[]>([]);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = async (q: string) => {
    if (!q.trim()) { setOpts([]); return; }
    setBusy(true);
    try {
      const res = await transportApi.searchConsignmentLookups(q, 1, 20);
      setOpts((res.items ?? []).map((i: any) => ({
        value: i.name as string,
        label: i.name as string,
        id: i.id as number,
        origin: (i.subtitle ?? "") as string,
        totalPackages: 0,
      })));
    } catch { /* silent */ }
    finally { setBusy(false); }
  };

  const onSearch = (q: string) => {
    onChange(q);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => void search(q), 250);
  };

  const onSelect = async (_: string, opt: any) => {
    const cnmtNo = opt.value as string;
    onChange(cnmtNo);
    // Fill source + packages from consignment record
    try {
      const c = await transportApi.getConsignment(opt.id as number);
      onFill(c.origin ?? "", c.totalPackages ?? 0);
    } catch { /* silent — user can fill manually */ }
    // Notify parent so it can auto-load manifest header if still empty
    onConsignmentSelected?.(cnmtNo);
  };

  return (
    <AutoComplete
      size="small"
      value={value}
      options={opts}
      onSearch={onSearch}
      onSelect={onSelect}
      onChange={onChange}
      placeholder="Type LR / Bilty no"
      style={{ width: "100%" }}
      filterOption={false}
      notFoundContent={busy ? <div style={{ padding: "6px 12px", textAlign: "center" }}><Spin size="small" /></div> : null}
    />
  );
};

// ─── Print report helpers ────────────────────────────────────────────────────

function openPrintWindow(html: string) {
  const win = window.open("", "_blank", "width=1100,height=750");
  if (!win) { alert("Allow pop-ups to print."); return; }
  win.document.write(html);
  win.document.close();
  win.onload = () => { win.focus(); win.print(); };
}

const PRINT_BASE_STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 10px; color: #000; padding: 12mm; }
  h1 { font-size: 15px; text-align: center; margin-bottom: 3px; }
  .sub { text-align: center; font-size: 11px; margin-bottom: 8px; color: #444; }
  .hdr { display: grid; grid-template-columns: repeat(4,1fr); gap: 4px 12px;
         border: 1px solid #000; padding: 6px 8px; margin-bottom: 10px; }
  .hdr .f { display: flex; flex-direction: column; }
  .hdr .lbl { font-size: 8px; color: #666; text-transform: uppercase; }
  .hdr .val { font-size: 11px; font-weight: 600; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
  th { background:#333; color:#fff; padding:4px 5px; text-align:left; font-size:9px; border:1px solid #555; }
  td { padding:3px 5px; border:1px solid #ccc; font-size:9px; vertical-align:top; }
  tr:nth-child(even) td { background:#f9f9f9; }
  tfoot td { font-weight:700; background:#eee; border-top:2px solid #000; }
  .num { text-align:right; } .center { text-align:center; }
  .badge { display:inline-block; padding:1px 6px; border-radius:3px; font-size:9px; font-weight:700; }
  .badge-red { background:#ffd6d6; color:#a00; }
  .badge-grn { background:#d6ffd6; color:#070; }
  .footer { margin-top:16px; display:grid; grid-template-columns:1fr 1fr 1fr; gap:8px; }
  .sig { border-top:1px solid #000; padding-top:4px; font-size:9px; text-align:center; color:#555; }
  @media print { body { padding:8mm; } @page { margin:8mm; size:A4 landscape; } }
`;

function buildHeaderHtml(v: any, arrivalNo: string, branchName: string) {
  return `
  <div class="hdr">
    <div class="f"><span class="lbl">Arrival No</span><span class="val">${arrivalNo}</span></div>
    <div class="f"><span class="lbl">Arrival Date</span><span class="val">${v.arrivalDate ? dayjs(v.arrivalDate).format("DD/MM/YYYY") : ""}</span></div>
    <div class="f"><span class="lbl">Challan No</span><span class="val">${v.challanNo ?? ""}</span></div>
    <div class="f"><span class="lbl">Challan Date</span><span class="val">${v.challanDate ? dayjs(v.challanDate).format("DD/MM/YYYY") : ""}</span></div>
    <div class="f"><span class="lbl">Receiving Branch</span><span class="val">${branchName}</span></div>
    <div class="f"><span class="lbl">From Station</span><span class="val">${v.fromStation ?? ""}</span></div>
    <div class="f"><span class="lbl">Lorry No</span><span class="val">${v.lorryNo ?? ""}</span></div>
    <div class="f"><span class="lbl">Arrived At</span><span class="val">${v.arrivedAt ?? ""}</span></div>
    <div class="f"><span class="lbl">Pkgs Despatched</span><span class="val">${v.pkgsDespatched ?? 0}</span></div>
    <div class="f"><span class="lbl">Pkgs Received</span><span class="val">${v.pkgsReceived ?? 0}</span></div>
    <div class="f"><span class="lbl">Short / Excess</span><span class="val">${(v.pkgsReceived ?? 0) - (v.pkgsDespatched ?? 0)}</span></div>
    <div class="f"><span class="lbl">Unloaded By</span><span class="val">${v.unloadedBy ?? ""}</span></div>
  </div>`;
}

function printArrivalSheet(arrivalNo: string, branchName: string, v: any, items: ArrivalItem[]) {
  const rows = items.filter(i => i.consignmentNo.trim()).map((i, idx) => `
    <tr>
      <td>${idx + 1}</td><td>${i.consignmentNo}</td><td>${i.source}</td>
      <td class="num">${i.noPackages}</td><td class="num">${i.short}</td>
      <td class="num">${i.excess}</td><td class="num">${i.damage}</td>
      <td class="center">${i.isExcess ? "E " : ""}${i.isTheft ? "T " : ""}${i.isSeized ? "S" : ""}</td>
      <td>${i.remarks}</td>
    </tr>`).join("");
  openPrintWindow(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Arrival Sheet — ${arrivalNo}</title>
    <style>${PRINT_BASE_STYLES}</style></head><body>
    <h1>MANIFEST ARRIVAL SHEET</h1><div class="sub">${arrivalNo}</div>
    ${buildHeaderHtml(v, arrivalNo, branchName)}
    <table><thead><tr><th>#</th><th>Consignment No</th><th>Source</th><th>Pkgs</th>
      <th>Short</th><th>Excess</th><th>Damage</th><th>E/T/S</th><th>Remarks</th></tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr><td colspan="3"><strong>TOTAL</strong></td>
      <td class="num">${items.reduce((s, i) => s + i.noPackages, 0)}</td>
      <td class="num">${items.reduce((s, i) => s + i.short, 0)}</td>
      <td class="num">${items.reduce((s, i) => s + i.excess, 0)}</td>
      <td class="num">${items.reduce((s, i) => s + i.damage, 0)}</td>
      <td colspan="2"></td></tr></tfoot></table>
    <div class="footer"><div class="sig">Prepared By</div><div class="sig">Checked By</div><div class="sig">Authorised Signatory</div></div>
    </body></html>`);
}

function printShortageReport(arrivalNo: string, branchName: string, v: any, items: ArrivalItem[]) {
  const affected = items.filter(i => i.consignmentNo.trim() && (i.short > 0 || i.excess > 0 || i.damage > 0 || i.isTheft || i.isSeized));
  if (affected.length === 0) { alert("No shortage or damage items to report."); return; }
  const rows = affected.map((i, idx) => `
    <tr>
      <td>${idx + 1}</td><td>${i.consignmentNo}</td><td>${i.source}</td>
      <td class="num">${i.noPackages}</td>
      <td class="num ${i.short > 0 ? "badge badge-red" : ""}">${i.short > 0 ? i.short : "-"}</td>
      <td class="num ${i.excess > 0 ? "badge badge-grn" : ""}">${i.excess > 0 ? `+${i.excess}` : "-"}</td>
      <td class="num ${i.damage > 0 ? "badge badge-red" : ""}">${i.damage > 0 ? i.damage : "-"}</td>
      <td class="center">${i.isTheft ? "<span class='badge badge-red'>THEFT</span> " : ""}${i.isSeized ? "<span class='badge badge-red'>SEIZED</span>" : ""}</td>
      <td>${i.remarks}</td>
    </tr>`).join("");
  openPrintWindow(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Shortage Report — ${arrivalNo}</title>
    <style>${PRINT_BASE_STYLES}</style></head><body>
    <h1>SHORTAGE &amp; DAMAGE REPORT</h1><div class="sub">${arrivalNo} — Discrepancy Items Only</div>
    ${buildHeaderHtml(v, arrivalNo, branchName)}
    <table><thead><tr><th>#</th><th>Consignment No</th><th>Source</th><th>Pkgs</th>
      <th>Short</th><th>Excess</th><th>Damage</th><th>Flags</th><th>Remarks</th></tr></thead>
    <tbody>${rows}</tbody></table>
    <div class="footer"><div class="sig">Verified By</div><div class="sig">Carrier Representative</div><div class="sig">Consignee / Agent</div></div>
    </body></html>`);
}

function printUnloadingReceipt(arrivalNo: string, branchName: string, v: any, items: ArrivalItem[]) {
  const rows = items.filter(i => i.consignmentNo.trim()).map((i, idx) => `
    <tr>
      <td>${idx + 1}</td><td>${i.consignmentNo}</td><td>${i.source}</td>
      <td class="num">${i.noPackages}</td>
      <td class="num">${i.short > 0 ? `<span class="badge badge-red">${i.short}</span>` : "-"}</td>
      <td>${v.godown ?? ""}</td><td></td>
    </tr>`).join("");
  openPrintWindow(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Unloading Receipt — ${arrivalNo}</title>
    <style>${PRINT_BASE_STYLES}</style></head><body>
    <h1>UNLOADING / GODOWN RECEIPT</h1><div class="sub">${arrivalNo}</div>
    ${buildHeaderHtml(v, arrivalNo, branchName)}
    <p style="font-size:10px;margin-bottom:6px;">Godown: <strong>${v.godown || "—"}</strong> &nbsp;|&nbsp; Unloaded By: <strong>${v.unloadedBy || "—"}</strong></p>
    <table><thead><tr><th>#</th><th>Consignment No</th><th>Source</th>
      <th>Pkgs Received</th><th>Short</th><th>Godown Bay</th><th>Initials</th></tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr><td colspan="3"><strong>TOTAL</strong></td>
      <td class="num">${items.reduce((s, i) => s + i.noPackages, 0)}</td>
      <td class="num">${items.reduce((s, i) => s + i.short, 0)}</td>
      <td colspan="2"></td></tr></tfoot></table>
    <div class="footer"><div class="sig">Checker</div><div class="sig">Store Keeper</div><div class="sig">Supervisor</div></div>
    </body></html>`);
}

function printDeliveryAdvice(arrivalNo: string, branchName: string, v: any, items: ArrivalItem[]) {
  const rows = items.filter(i => i.consignmentNo.trim()).map((i, idx) => `
    <tr>
      <td>${idx + 1}</td><td>${i.consignmentNo}</td><td>${i.source}</td>
      <td class="num">${i.noPackages}</td><td></td><td></td><td></td>
    </tr>`).join("");
  openPrintWindow(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Delivery Advice — ${arrivalNo}</title>
    <style>${PRINT_BASE_STYLES}</style></head><body>
    <h1>DELIVERY ADVICE</h1><div class="sub">${arrivalNo} — ${branchName}</div>
    ${buildHeaderHtml(v, arrivalNo, branchName)}
    <table><thead><tr><th>#</th><th>Consignment No</th><th>Source</th>
      <th>Pkgs</th><th>Consignee</th><th>Delivery Date</th><th>Sign</th></tr></thead>
    <tbody>${rows}</tbody></table>
    <div class="footer"><div class="sig">Delivery In-charge</div><div class="sig">Driver / Agent</div><div class="sig">Authorised Signatory</div></div>
    </body></html>`);
}

// ─── Print Reports Modal ─────────────────────────────────────────────────────

const PrintReportsModal = ({
  open, onClose, arrivalNo, formValues, items, branchName,
}: {
  open: boolean;
  onClose: () => void;
  arrivalNo: string;
  formValues: any;
  items: ArrivalItem[];
  branchName: string;
}) => {
  const reports = [
    {
      title: "Arrival Sheet",
      description: "Full arrival record — all consignments, package counts, short/excess/damage summary and signatures.",
      icon: "📋",
      action: () => printArrivalSheet(arrivalNo, branchName, formValues, items),
    },
    {
      title: "Shortage & Damage Report",
      description: "Only items with discrepancies (short, excess, damage, theft, seized). For insurance or claims.",
      icon: "⚠️",
      action: () => printShortageReport(arrivalNo, branchName, formValues, items),
    },
    {
      title: "Unloading / Godown Receipt",
      description: "Checklist for warehouse staff — includes godown bay and checker initials column.",
      icon: "🏭",
      action: () => printUnloadingReceipt(arrivalNo, branchName, formValues, items),
    },
    {
      title: "Delivery Advice",
      description: "One-page dispatch slip sent with the consignments to the consignee or delivery agent.",
      icon: "🚚",
      action: () => printDeliveryAdvice(arrivalNo, branchName, formValues, items),
    },
  ];

  return (
    <Modal
      title={<><PrinterOutlined style={{ marginRight: 8 }} />Print Reports — {arrivalNo}</>}
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
      width={560}
    >
      <Typography.Text type="secondary" style={{ display: "block", marginBottom: 16, fontSize: 12 }}>
        This arrival is processed. Choose a report to print:
      </Typography.Text>
      <Space direction="vertical" style={{ width: "100%" }} size={10}>
        {reports.map(r => (
          <div
            key={r.title}
            style={{
              display: "flex", alignItems: "center", gap: 12,
              border: "1px solid #e8e8e8", borderRadius: 6, padding: "10px 14px",
              cursor: "pointer", transition: "border-color 0.2s",
            }}
            onMouseEnter={e => (e.currentTarget.style.borderColor = "#1677ff")}
            onMouseLeave={e => (e.currentTarget.style.borderColor = "#e8e8e8")}
            onClick={() => { r.action(); }}
          >
            <span style={{ fontSize: 24 }}>{r.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{r.title}</div>
              <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{r.description}</div>
            </div>
            <Button size="small" icon={<PrinterOutlined />}>Print</Button>
          </div>
        ))}
      </Space>
    </Modal>
  );
};

export const ManifestArrivalPage = () => {
  const workspace = useOperationsWorkspace();
  const [rows, setRows]           = useState<ArrivalSummary[]>([]);
  const [loading, setLoading]     = useState(false);
  const [search, setSearch]       = useState("");

  // ── Form state ───────────────────────────────────────────────────────────
  const [form]     = Form.useForm();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving]       = useState(false);
  const [formOpen, setFormOpen]   = useState(false);
  const [createTempKey, setCreateTempKey] = useState<string | null>(null);

  // Grid items (editable inline)
  const [items, setItems] = useState<ArrivalItem[]>([]);

  // Manifest lookup
  const [lookupLoading, setLookupLoading] = useState(false);
  const [linkedManifestId, setLinkedManifestId] = useState<number | null>(null);
  const [arrivalTransportMode, setArrivalTransportMode] = useState<"Road" | "Rail">("Road");

  // Print report modal — opens after Save & Mark Processed, or via Print button on processed record
  const [printModalOpen, setPrintModalOpen] = useState(false);

  // Manifest search AutoComplete (quick-load bar at form top)
  type ManifestSuggest = {
    value: string;
    label: React.ReactNode;
    manifestNo: string;
  };
  const [manifestOpts, setManifestOpts]               = useState<ManifestSuggest[]>([]);
  const [manifestSearchVal, setManifestSearchVal]     = useState("");
  const [manifestSearchLoading, setManifestSearchLoading] = useState(false);

  const searchRef = useRef<any>(null);
  const challanRef = useRef<any>(null);
  // tracks whether the form was just opened fresh (for autoFocus on branch select)
  const [justOpenedNew, setJustOpenedNew] = useState(false);

  const branchFetcher = useCallback(
    (q: string, page: number, pageSize: number) => transportApi.searchBranchLookups(q, page, pageSize),
    []
  );

  // Reactive branchId from form — needed to pass to EntityAttachmentsPanel in create mode
  const formBranchId = Form.useWatch("branchId", form) as number | undefined;

  // Inline branch create
  const [branchCreateOpen, setBranchCreateOpen] = useState(false);
  const [branchPrefill, setBranchPrefill]       = useState("");
  const [branchSeeds, setBranchSeeds]           = useState<LookupItem[]>([]);

  // ── Keyboard shortcuts ───────────────────────────────────────────────────
  // List-view shortcuts — active only when form is NOT open
  usePageKeyboardShortcuts({
    "alt+n": () => openNew(),
    "f5":    () => void load(),
    "f3":    () => searchRef.current?.focus(),
  }, !formOpen);

  // Form-view shortcuts — active only when form IS open
  const cancelForm = () => {
    if (editingId === null && createTempKey) void transportApi.deleteAttachmentsByTempKey(createTempKey);
    setCreateTempKey(null);
    setFormOpen(false);
    form.resetFields();
    setItems([]);
  };

  usePageKeyboardShortcuts({
    "ctrl+s":  () => void onSave(),
    "alt+w":  () => cancelForm(),
  }, formOpen);

  // ── Load list ────────────────────────────────────────────────────────────
  const load = async () => {
    setLoading(true);
    try {
      const result = await transportApi.listManifestArrivals({ q: search.trim() || undefined, pageSize: 200 });
      setRows(result.data as ArrivalSummary[]);
    } catch (err) {
      message.error(getApiErrorMessage(err, "Unable to load manifest arrivals"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (workspace?.mode === "data") void load(); }, [workspace?.mode]);

  // ── Open new form ────────────────────────────────────────────────────────
  const openNew = () => {
    setEditingId(null);
    setLinkedManifestId(null);
    setArrivalTransportMode("Road");
    form.resetFields();
    form.setFieldsValue({
      arrivalDate:  dayjs(),
      documentDate: dayjs(),
      challanDate:  dayjs(),
    });
    setItems([]);
    setCreateTempKey(crypto.randomUUID());
    setJustOpenedNew(true);
    setFormOpen(true);
  };

  useEffect(() => {
    if (workspace?.mode === "entry") openNew();
    else setFormOpen(false);
  }, [workspace?.mode]);

  // ── Open existing for edit / process ────────────────────────────────────
  const openEdit = async (row: ArrivalSummary) => {
    setFormOpen(true);
    setEditingId(row.id);
    try {
      const detail: ArrivalDetail = await transportApi.getManifestArrival(row.id);
      setLinkedManifestId(detail.manifestId ?? null);
      setArrivalTransportMode(
        String(detail.transportMode ?? "").toLowerCase() === "rail" ||
        !!detail.railwayReceiptNo || !!detail.trainNo || !!detail.wagonNo ||
        (!!detail.manifestId && !detail.lorryNo?.trim()) ? "Rail" : "Road"
      );

      // Seed the branch LookupSelect so it shows the name instead of a raw ID
      if (detail.branchId && detail.branchName) {
        setBranchSeeds([{ id: detail.branchId, label: detail.branchName }]);
      }

      form.setFieldsValue({
        branchId:       detail.branchId,
        challanNo:      detail.challanNo,
        challanDate:    dayjs(detail.challanDate),
        arrivalDate:    dayjs(detail.arrivalDate),
        documentDate:   dayjs(detail.documentDate),
        arrivedAt:      detail.arrivedAt,
        fromStation:    detail.fromStation,
        lorryNo:        detail.lorryNo,
        transportMode:  String(detail.transportMode ?? "").toLowerCase() === "rail" ||
          !!detail.railwayReceiptNo || !!detail.trainNo || !!detail.wagonNo ||
          (!!detail.manifestId && !detail.lorryNo?.trim()) ? "Rail" : "Road",
        railwayReceiptNo: detail.railwayReceiptNo,
        trainNo:        detail.trainNo,
        wagonNo:        detail.wagonNo,
        unloadedBy:     detail.unloadedBy ?? "",
        pkgsDespatched: detail.pkgsDespatched,
        pkgsReceived:   detail.pkgsReceived,
        godown:         detail.godown ?? "",
        amount:         detail.amount,
        remarks:        detail.remarks ?? "",
      });
      setItems(itemsFromDetail(detail));
    } catch (err) {
      message.error(getApiErrorMessage(err, "Unable to load arrival details"));
      setFormOpen(false);
    }
  };

  // ── Shared: apply a manifest lookup result to the form & grid ────────────
  const applyManifestResult = (result: any) => {
    setLinkedManifestId(result.manifestId);

    const reportedMode = String(result.transportMode ?? "").toLowerCase();
    const resolvedTransportMode = reportedMode === "rail" || reportedMode === "train" ||
      !!result.railwayReceiptNo || !!result.trainNo || !!result.wagonNo || !String(result.lorryNo ?? "").trim()
      ? "Rail"
      : "Road";
    setArrivalTransportMode(resolvedTransportMode);

    const updates: Record<string, any> = {
      challanNo:      form.getFieldValue("challanNo") || result.manifestNo,
      lorryNo:        result.lorryNo,
      transportMode:  resolvedTransportMode,
      railwayReceiptNo: result.railwayReceiptNo,
      trainNo:        result.trainNo,
      wagonNo:        result.wagonNo,
      fromStation:    result.fromStation,
      pkgsDespatched: result.pkgsDespatched,
      challanDate:    dayjs(result.dispatchDate),
      pkgsReceived:   result.pkgsDespatched,
    };

    // Auto-populate the Receiving Branch from the manifest's destination branch
    if (result.toBranchId) {
      updates.branchId = result.toBranchId;
      if (result.toBranchName) {
        setBranchSeeds(prev => [
          { id: result.toBranchId as number, label: result.toBranchName as string },
          ...prev.filter(x => x.id !== result.toBranchId),
        ]);
      }
    }

    form.setFieldsValue(updates);
    setItems(result.items.map((i: any) => ({ ...blankItem(), key: newKey(), ...i, remarks: "" })));
    message.success(`Loaded ${result.items.length} consignment(s) from manifest ${result.manifestNo}`);
  };

  // ── Lookup manifest by challan/manifest number (manual entry + button) ───
  const lookupManifest = async () => {
    const challanNo: string = (form.getFieldValue("challanNo") ?? "").trim();
    if (!challanNo) { message.warning("Enter a Challan / Manifest number first"); return; }

    setLookupLoading(true);
    try {
      const result = await transportApi.lookupManifestForArrival(challanNo);
      applyManifestResult(result);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        message.warning("No manifest found with that challan number — fill details manually");
        setLinkedManifestId(null);
      } else {
        message.error(getApiErrorMessage(err, "Lookup failed"));
      }
    } finally {
      setLookupLoading(false);
    }
  };

  // ── Lookup manifest by manifest number (called from AutoComplete) ─────────
  const lookupManifestByNo = async (manifestNo: string) => {
    setLookupLoading(true);
    try {
      const result = await transportApi.lookupManifestForArrival(manifestNo);
      form.setFieldValue("challanNo", result.manifestNo);
      applyManifestResult(result);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        message.warning("Manifest not found");
      } else {
        message.error(getApiErrorMessage(err, "Lookup failed"));
      }
    } finally {
      setLookupLoading(false);
    }
  };

  // ── Lookup manifest by a consignment number (auto-triggered from grid) ────
  // Only fires when the arrival header fields are still empty.
  const lookupManifestByConsignment = async (consignmentNo: string) => {
    setLookupLoading(true);
    try {
      const result = await transportApi.lookupManifestByConsignment(consignmentNo);
      form.setFieldValue("challanNo", result.manifestNo);
      applyManifestResult(result);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        // Consignment not in any manifest — perfectly normal, ignore silently
      } else {
        message.warning("Could not auto-load manifest for this consignment");
      }
    } finally {
      setLookupLoading(false);
    }
  };

  // ── Search manifests for the quick-load AutoComplete ─────────────────────
  const searchManifests = async (q: string) => {
    setManifestSearchLoading(true);
    try {
      const items: any[] = await transportApi.searchManifestSuggestions(q);
      setManifestOpts(items.map(m => ({
        value: m.manifestNo as string,
        manifestNo: m.manifestNo as string,
        label: (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <span>
              <strong>{m.manifestNo}</strong>
              {m.lorryNo && (
                <span style={{ marginLeft: 8, fontFamily: "monospace", color: "#555" }}>{m.lorryNo}</span>
              )}
            </span>
            <span style={{ fontSize: 11, color: "#888", whiteSpace: "nowrap" }}>
              {dayjs(m.dispatchDate as string).format("DD/MM/YY")}
              {m.fromBranchName && <> &nbsp;·&nbsp; {m.fromBranchName}</>}
              {" "}· {m.pkgsCount} pkgs
            </span>
          </div>
        ),
      })));
    } catch { /* silent */ }
    finally { setManifestSearchLoading(false); }
  };

  // ── Item grid helpers ─────────────────────────────────────────────────────
  const updateItem = (key: string, field: keyof ArrivalItem, value: any) =>
    setItems(prev => prev.map(i => {
      if (i.key !== key) return i;
      if (field === "short") return { ...i, short: value, excess: value > 0 ? 0 : i.excess };
      if (field === "excess") return { ...i, excess: value, short: value > 0 ? 0 : i.short };
      return { ...i, [field]: value };
    }));

  const updateItemPartial = (key: string, partial: Partial<ArrivalItem>) =>
    setItems(prev => prev.map(i => i.key === key ? { ...i, ...partial } : i));

  const removeItem = (key: string) =>
    setItems(prev => prev.filter(i => i.key !== key));

  const syncDispatchedTotal = (value: number) => {
    setItems((current) => {
      if (current.length === 0) return current;
      const total = current.reduce((sum, item) => sum + (item.noPackages || 0), 0);
      const delta = value - total;
      return current.map((item, index) => index === 0
        ? { ...item, noPackages: Math.max(0, item.noPackages + delta) }
        : item);
    });
  };

  const syncReceivedTotal = (value: number) => {
    setItems((current) => {
      if (current.length === 0) return current;
      const totalPackages = current.reduce((sum, item) => sum + (item.noPackages || 0), 0);
      const otherNet = current.slice(1).reduce((sum, item) => sum + (item.excess || 0) - (item.short || 0), 0);
      const firstNet = value - totalPackages - otherNet;
      return current.map((item, index) => index === 0
        ? { ...item, short: firstNet < 0 ? -firstNet : 0, excess: firstNet > 0 ? firstNet : 0 }
        : item);
    });
  };


  // Recalculate totals from grid whenever items change
  useEffect(() => {
    const totalPkgs    = items.reduce((s, i) => s + (i.noPackages || 0), 0);
    const received     = items.reduce((s, i) => s + (i.noPackages || 0) - (i.short || 0) + (i.excess || 0), 0);
    form.setFieldsValue({ pkgsDespatched: totalPkgs, pkgsReceived: received });
    // ShortExcess is displayed inline, not a form field
  }, [items, form]);

  // ── Build save payload ────────────────────────────────────────────────────
  const buildPayload = (values: any, markAsProcessed = false) => ({
    ...getDefaultContext(),
    enteredBy:      getEnteredByUser(),
    arrivalDate:    values.arrivalDate.toISOString(),
    documentDate:   values.documentDate.toISOString(),
    challanNo:      values.challanNo,
    challanDate:    values.challanDate.toISOString(),
    branchId:       values.branchId,
    arrivedAt:      values.arrivedAt ?? "",
    fromStation:    values.fromStation ?? "",
    lorryNo:        values.lorryNo,
    unloadedBy:     values.unloadedBy ?? null,
    pkgsDespatched: values.pkgsDespatched ?? 0,
    pkgsReceived:   values.pkgsReceived ?? 0,
    godown:         values.godown ?? null,
    amount:         values.amount ?? 0,
    manifestId:     linkedManifestId,
    remarks:        values.remarks ?? null,
    markAsProcessed,
    // Only send tempKey on create — never on update.
    tempKey:        editingId === null ? (createTempKey ?? undefined) : undefined,
    items: items
      .filter(i => i.consignmentNo.trim())
      .map(i => ({
        consignmentNo: i.consignmentNo,
        source:        i.source,
        noPackages:    i.noPackages,
        short:         i.short,
        excess:        i.excess,
        damage:        i.damage,
        isExcess:      i.isExcess,
        isTheft:       i.isTheft,
        isSeized:      i.isSeized,
        remarks:       i.remarks || null,
      })),
  });

  // ── Save (plain — no status change) ──────────────────────────────────────
  const onSave = async (markAsProcessed = false) => {
    let values: any;
    try { values = await form.validateFields(); }
    catch { return; }

    if (items.length === 0 || items.every(i => !i.consignmentNo.trim())) {
      message.warning("Add at least one consignment row before saving");
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload(values, markAsProcessed);

      if (editingId !== null) {
        await transportApi.updateManifestArrival(editingId, payload);
        message.success(markAsProcessed ? "Arrival saved and marked as Processed ✓" : "Manifest arrival updated");
      } else {
        await transportApi.createManifestArrival(payload);
        // Clear tempKey without deleting — files are now bound server-side.
        setCreateTempKey(null);
        message.success(markAsProcessed ? "Arrival recorded and marked as Processed ✓" : "Manifest arrival recorded");
      }

      await load();

      if (markAsProcessed) {
        // Stay on the form and show the print reports dialog
        setPrintModalOpen(true);
      } else {
        setFormOpen(false);
        form.resetFields();
        setItems([]);
      }
    } catch (err) {
      message.error(getApiErrorMessage(err, "Unable to save"));
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const onDelete = async (id: number) => {
    try {
      await transportApi.deleteManifestArrival(id);
      message.success("Arrival record deleted");
      if (editingId === id) { setFormOpen(false); form.resetFields(); }
      await load();
    } catch (err) {
      message.error(getApiErrorMessage(err, "Unable to delete"));
    }
  };

  // ── Print ─────────────────────────────────────────────────────────────────
  const printArrival = () => {
    const values = form.getFieldsValue();
    const totalPkgsDespatch = values.pkgsDespatched ?? 0;
    const totalPkgsReceived = values.pkgsReceived ?? 0;
    const shortExcess = totalPkgsReceived - totalPkgsDespatch;

    const rowsHtml = items
      .filter(i => i.consignmentNo.trim())
      .map((i, idx) => `
        <tr>
          <td>${idx + 1}</td>
          <td>${i.consignmentNo}</td>
          <td>${i.source}</td>
          <td class="num">${i.noPackages}</td>
          <td class="num">${i.short}</td>
          <td class="num">${i.excess}</td>
          <td class="num">${i.damage}</td>
          <td>${i.remarks}</td>
          <td class="center">
            ${i.isExcess ? "E" : ""}${i.isTheft ? "T" : ""}${i.isSeized ? "S" : ""}
          </td>
        </tr>`).join("");

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Manifest Arrival — ${editingId ?? "NEW"}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 10px; color: #000; padding: 12mm; }
    h1 { font-size: 16px; text-align: center; margin-bottom: 4px; }
    .sub { text-align: center; font-size: 11px; margin-bottom: 10px; color: #444; }
    .hdr { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px 12px;
           border: 1px solid #000; padding: 6px 8px; margin-bottom: 10px; }
    .hdr .f { display: flex; flex-direction: column; }
    .hdr .lbl { font-size: 8px; color: #666; text-transform: uppercase; }
    .hdr .val { font-size: 11px; font-weight: 600; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
    th { background: #333; color: #fff; padding: 4px 5px; text-align: left; font-size: 9px; border: 1px solid #555; }
    td { padding: 3px 5px; border: 1px solid #ccc; font-size: 9px; vertical-align: top; }
    tr:nth-child(even) td { background: #f9f9f9; }
    tfoot td { font-weight: 700; background: #eee; border-top: 2px solid #000; }
    .num { text-align: right; } .center { text-align: center; }
    .footer { margin-top: 16px; display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; }
    .sig { border-top: 1px solid #000; padding-top: 4px; font-size: 9px; text-align: center; color: #555; }
    @media print { body { padding: 8mm; } @page { margin: 8mm; size: A4 landscape; } }
  </style>
</head>
<body>
  <h1>MANIFEST ARRIVAL</h1>
  <div class="sub">Arrival Record</div>
  <div class="hdr">
    <div class="f"><span class="lbl">Challan No</span><span class="val">${values.challanNo ?? ""}</span></div>
    <div class="f"><span class="lbl">Arrival Date</span><span class="val">${values.arrivalDate ? dayjs(values.arrivalDate).format("DD/MM/YYYY") : ""}</span></div>
    <div class="f"><span class="lbl">Lorry No</span><span class="val">${values.lorryNo ?? ""}</span></div>
    <div class="f"><span class="lbl">From Station</span><span class="val">${values.fromStation ?? ""}</span></div>
    <div class="f"><span class="lbl">Arrived At</span><span class="val">${values.arrivedAt ?? ""}</span></div>
    <div class="f"><span class="lbl">Unloaded By</span><span class="val">${values.unloadedBy ?? ""}</span></div>
    <div class="f"><span class="lbl">Pkgs Despatched</span><span class="val">${totalPkgsDespatch}</span></div>
    <div class="f"><span class="lbl">Pkgs Received</span><span class="val">${totalPkgsReceived}</span></div>
    <div class="f"><span class="lbl">Short / Excess</span><span class="val">${shortExcess}</span></div>
    <div class="f"><span class="lbl">Godown</span><span class="val">${values.godown ?? ""}</span></div>
    <div class="f"><span class="lbl">Amount</span><span class="val">₹ ${(values.amount ?? 0).toFixed(2)}</span></div>
  </div>
  <table>
    <thead>
      <tr>
        <th>#</th><th>Consignment No</th><th>Source</th>
        <th>Pkgs</th><th>Short</th><th>Excess</th><th>Damage</th>
        <th>Remarks</th><th>E/T/S</th>
      </tr>
    </thead>
    <tbody>${rowsHtml}</tbody>
    <tfoot>
      <tr>
        <td colspan="3"><strong>TOTAL</strong></td>
        <td class="num">${items.reduce((s, i) => s + i.noPackages, 0)}</td>
        <td class="num">${items.reduce((s, i) => s + i.short, 0)}</td>
        <td class="num">${items.reduce((s, i) => s + i.excess, 0)}</td>
        <td class="num">${items.reduce((s, i) => s + i.damage, 0)}</td>
        <td colspan="2"></td>
      </tr>
    </tfoot>
  </table>
  <div class="footer">
    <div class="sig">Prepared By</div>
    <div class="sig">Checked By</div>
    <div class="sig">Authorised Signatory</div>
  </div>
</body>
</html>`;

    const win = window.open("", "_blank", "width=1100,height=750");
    if (!win) { alert("Allow pop-ups to print."); return; }
    win.document.write(html);
    win.document.close();
    win.onload = () => { win.focus(); win.print(); };
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const filteredRows = rows.filter(r => {
    const t = search.trim().toLowerCase();
    if (!t) return true;
    return [r.arrivalNo, r.challanNo, r.lorryNo, r.fromStation].some(v => v.toLowerCase().includes(t));
  });

  const pkgsDespatched = Form.useWatch("pkgsDespatched", form) ?? 0;
  const pkgsReceived   = Form.useWatch("pkgsReceived", form) ?? 0;
  const shortExcess    = pkgsReceived - pkgsDespatched;

  // ── Render ────────────────────────────────────────────────────────────────
  if (formOpen) {
    // Clear the autoFocus flag after first render of the form
    if (justOpenedNew) setTimeout(() => setJustOpenedNew(false), 0);
    return (
      <Card className="panel-card">
        {/* ── Form header bar ── */}
        {(() => {
          const editingRow = editingId ? rows.find(r => r.id === editingId) : null;
          return (
            <div className="panel-title-row" style={{ marginBottom: 16 }}>
              <Space align="center" size={10}>
                <Typography.Title level={4} style={{ margin: 0 }}>
                  {editingId
                    ? `Arrival — ${editingRow?.arrivalNo ?? `#${editingId}`}`
                    : "New Manifest Arrival"}
                </Typography.Title>
                {editingRow?.isProcessed ? (
                  <Tag color="green" style={{ fontSize: 12 }}>
                    ✓ Processed{editingRow.processedAt
                      ? ` · ${dayjs(editingRow.processedAt).format("DD/MM/YY HH:mm")}`
                      : ""}
                  </Tag>
                ) : editingId ? (
                  <Tag color="orange">Pending</Tag>
                ) : null}
              </Space>
              <Space>
                <span className="kbd-hint"><kbd>Ctrl</kbd>+<kbd>S</kbd> Save &nbsp;·&nbsp; <kbd>Alt</kbd>+<kbd>W</kbd> Close</span>
                <Button onClick={cancelForm}>
                  ← Back to List
                </Button>
              </Space>
            </div>
          );
        })()}

        <Form form={form} layout="vertical">
          {/* ── Quick-load manifest search bar ── */}
          <Card
            size="small"
            style={{ marginBottom: 16, background: "#f0f7ff", border: "1px solid #91caff" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <ThunderboltOutlined style={{ color: "#1677ff" }} />
              <span style={{ fontWeight: 600, color: "#1677ff" }}>Quick Load from Manifest</span>
              <span style={{ fontSize: 12, color: "#888" }}>— type manifest no or lorry no to auto-fill all fields</span>
            </div>
            <AutoComplete
              disabled={!!linkedManifestId}
              value={manifestSearchVal}
              options={manifestOpts}
              onSearch={(q) => { setManifestSearchVal(q); void searchManifests(q); }}
              onSelect={(val: string) => {
                setManifestSearchVal("");
                setManifestOpts([]);
                void lookupManifestByNo(val);
              }}
              onFocus={() => { if (manifestOpts.length === 0) void searchManifests(""); }}
              placeholder="Search by Manifest No or Lorry No…"
              notFoundContent={
                manifestSearchLoading
                  ? <div style={{ padding: "6px 12px", textAlign: "center" }}><Spin size="small" /></div>
                  : <span style={{ padding: "6px 12px", color: "#888" }}>No manifests found</span>
              }
              filterOption={false}
              style={{ width: "100%" }}
              allowClear
              onClear={() => { setManifestSearchVal(""); setManifestOpts([]); }}
            />
          </Card>

          {/* ── Header fields – two columns mirroring the screenshot ── */}
          <Row gutter={24}>
            {/* Left column */}
            <Col span={12}>
              <Card size="small" style={{ marginBottom: 12 }}>
                <Form.Item name="branchId" label="Branch Code (Receiving Branch)"
                  rules={[{ required: true, message: "Select receiving branch" }]}>
                  <LookupSelect
                    disabled={!!linkedManifestId}
                    fetcher={branchFetcher}
                    placeholder="Search branch..."
                    addNewLabel="Branch"
                    seededOptions={branchSeeds}
                    onAddNew={(q) => { setBranchPrefill(q); setBranchCreateOpen(true); }}
                    autoFocus={justOpenedNew}
                  />
                </Form.Item>

                <Row gutter={12} align="bottom">
                  <Col flex="1">
                    <Form.Item name="challanNo" label="Challan No (Manifest No)"
                      rules={[{ required: true, message: "Enter challan / manifest number" }]}>
                      <Input ref={challanRef} placeholder="e.g. 0550040261"
                        readOnly={!!linkedManifestId}
                        onPressEnter={() => void lookupManifest()} />
                    </Form.Item>
                  </Col>
                  <Col>
                    <Form.Item label=" ">
                      <Tooltip placement="bottom" title="Search and load consignments from this manifest">
                        <Button
                          disabled={!!linkedManifestId}
                          icon={<QuestionCircleOutlined />}
                          loading={lookupLoading}
                          onClick={() => void lookupManifest()}
                        >
                          Load from Manifest
                        </Button>
                      </Tooltip>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="arrivalDate" label="Arrival Date" rules={[{ required: true }]}>
                      <DatePicker disabled={!!linkedManifestId} style={{ width: "100%" }} format="DD/MM/YYYY" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="documentDate" label="Document Date" rules={[{ required: true }]}>
                      <DatePicker disabled={!!linkedManifestId} style={{ width: "100%" }} format="DD/MM/YYYY" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="challanDate" label="Challan Date" rules={[{ required: true }]}>
                      <DatePicker disabled={!!linkedManifestId} style={{ width: "100%" }} format="DD/MM/YYYY" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="arrivedAt" label="Arrived At (Transit Point)"
                      tooltip="Intermediate transit point e.g. U P BORDER">
                      <Input readOnly={!!linkedManifestId} placeholder="e.g. U P BORDER" />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>

            {/* Right column */}
            <Col span={12}>
              <Card size="small" style={{ marginBottom: 12 }}>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="fromStation" label="From Station (Origin)"
                      tooltip="Origin station or branch">
                      <LocationAutoComplete disabled={!!linkedManifestId} placeholder="e.g. KAMLA MARKET" fieldLabel="From Station" />
                    </Form.Item>
                  </Col>
                  {arrivalTransportMode === "Rail" ? (
                    <Col span={12}>
                      <Form.Item label="Transport Mode"><Input value="Rail / Train" readOnly /></Form.Item>
                    </Col>
                  ) : (
                    <Col span={12}>
                      <Form.Item name="lorryNo" label="Lorry No" rules={[{ required: true, message: "Enter lorry number" }]}>
                        <Input readOnly={!!linkedManifestId} placeholder="e.g. DL01LAA0338" style={{ textTransform: "uppercase" }}
                          onChange={e => form.setFieldValue("lorryNo", e.target.value.toUpperCase())} />
                      </Form.Item>
                    </Col>
                  )}
                </Row>

                {arrivalTransportMode === "Rail" && (
                  <Row gutter={12}>
                    <Col span={8}><Form.Item name="railwayReceiptNo" label="RR No"><Input readOnly /></Form.Item></Col>
                    <Col span={8}><Form.Item name="trainNo" label="Train No"><Input readOnly /></Form.Item></Col>
                    <Col span={8}><Form.Item name="wagonNo" label="Wagon No"><Input readOnly /></Form.Item></Col>
                  </Row>
                )}

                <Form.Item name="unloadedBy" label="Unloaded By">
                  <Input placeholder="Name of person who unloaded" />
                </Form.Item>

                <Row gutter={12}>
                  <Col span={8}>
                    <Form.Item name="pkgsDespatched" label="Pkgs Despatched">
                      <InputNumber style={{ width: "100%" }} min={0} onChange={(value) => syncDispatchedTotal(value ?? 0)} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="pkgsReceived" label="Pkgs Received">
                      <InputNumber style={{ width: "100%" }} min={0} onChange={(value) => syncReceivedTotal(value ?? 0)} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Short / Excess">
                      <InputNumber
                        style={{
                          width: "100%",
                          color: shortExcess < 0 ? "#cf1322" : shortExcess > 0 ? "#389e0d" : undefined,
                          fontWeight: shortExcess !== 0 ? 700 : undefined,
                        }}
                        value={shortExcess}
                        readOnly
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="godown" label="Godown">
                      <Input placeholder="Godown / storage location" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="amount" label="Amount (₹)" initialValue={0}>
                      <InputNumber style={{ width: "100%" }} min={0} precision={2} />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          {linkedManifestId && (
            <Alert
              type="success" showIcon style={{ marginBottom: 12 }}
              message={`Linked to Manifest ID ${linkedManifestId}. Consignments loaded from manifest.`}
            />
          )}

          {/* ── Consignment grid ── */}
          <div style={{ marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography.Text strong>Consignment Details</Typography.Text>
          </div>

          {/* Columns: S#(40) + CnmtNo(190) + Source(130) + Pkgs(72) + Short(68) + Excess(68) + Damage(68) + Remarks(150) + ETS(100) + Del(40) = 926 */}
          <Table
            className="entry-inline-table"
            rowKey="key"
            size="small"
            dataSource={items}
            pagination={false}
            tableLayout="fixed"
            scroll={{ x: 926 }}
            style={{ marginBottom: 16, display: "block" }}
            summary={() => (
              <Table.Summary.Row style={{ fontWeight: 700 }}>
                {/* colSpan=3 covers S#, Consignment No, Source */}
                <Table.Summary.Cell index={0} colSpan={3}>TOTAL</Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">{items.reduce((s, i) => s + i.noPackages, 0)}</Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">{items.reduce((s, i) => s + i.short, 0)}</Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">{items.reduce((s, i) => s + i.excess, 0)}</Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="right">{items.reduce((s, i) => s + i.damage, 0)}</Table.Summary.Cell>
                <Table.Summary.Cell index={7} colSpan={3} />
              </Table.Summary.Row>
            )}
            columns={[
              {
                title: "#", width: 40,
                render: (_, __, idx) => idx + 1,
              },
              {
                title: "Consignment No", width: 190,
                render: (_, row) => linkedManifestId ? (
                  <Input size="small" value={row.consignmentNo} readOnly />
                ) : (
                  <ConsignmentRowCell
                    value={row.consignmentNo}
                    onChange={v => updateItem(row.key, "consignmentNo", v)}
                    onFill={(origin, pkgs) =>
                      updateItemPartial(row.key, { source: origin, noPackages: pkgs })
                    }
                    onConsignmentSelected={(cnmtNo) => {
                      const lorryNo = (form.getFieldValue("lorryNo") ?? "") as string;
                      if (!lorryNo.trim()) void lookupManifestByConsignment(cnmtNo);
                    }}
                  />
                ),
              },
              {
                title: "Source / Origin", width: 130,
                render: (_, row) => (
                  <Input
                    size="small" value={row.source}
                    readOnly={!!linkedManifestId}
                    onChange={e => updateItem(row.key, "source", e.target.value)}
                    placeholder="Origin"
                  />
                ),
              },
              {
                title: "Pkgs", width: 72, align: "right",
                render: (_, row) => (
                  <InputNumber
                    size="small" value={row.noPackages} min={0} disabled={!!linkedManifestId} style={{ width: "100%" }}
                    onChange={v => updateItem(row.key, "noPackages", v ?? 0)}
                  />
                ),
              },
              {
                title: "Short", width: 68, align: "right",
                render: (_, row) => (
                  <InputNumber
                    size="small" value={row.short} min={0} style={{ width: "100%" }}
                    onChange={v => updateItem(row.key, "short", v ?? 0)}
                  />
                ),
              },
              {
                title: "Excess", width: 68, align: "right",
                render: (_, row) => (
                  <InputNumber
                    size="small" value={row.excess} min={0} style={{ width: "100%" }}
                    onChange={v => updateItem(row.key, "excess", v ?? 0)}
                  />
                ),
              },
              {
                title: "Damage", width: 68, align: "right",
                render: (_, row) => (
                  <InputNumber
                    size="small" value={row.damage} min={0} style={{ width: "100%" }}
                    onChange={v => updateItem(row.key, "damage", v ?? 0)}
                  />
                ),
              },
              {
                title: "Remarks", width: 150,
                render: (_, row) => (
                  <Input
                    size="small" value={row.remarks}
                    onChange={e => updateItem(row.key, "remarks", e.target.value)}
                  />
                ),
              },
              {
                title: "E / T / S", width: 100, align: "center",
                render: (_, row) => (
                  <Space size={2}>
                    <Tooltip placement="bottom" title="Excess / pilferage">
                      <Checkbox disabled={!!linkedManifestId} checked={row.isExcess} onChange={e => updateItem(row.key, "isExcess", e.target.checked)}>E</Checkbox>
                    </Tooltip>
                    <Tooltip placement="bottom" title="Theft">
                      <Checkbox disabled={!!linkedManifestId} checked={row.isTheft} onChange={e => updateItem(row.key, "isTheft", e.target.checked)}>T</Checkbox>
                    </Tooltip>
                    <Tooltip placement="bottom" title="Seized">
                      <Checkbox disabled={!!linkedManifestId} checked={row.isSeized} onChange={e => updateItem(row.key, "isSeized", e.target.checked)}>S</Checkbox>
                    </Tooltip>
                  </Space>
                ),
              },
              {
                title: "", width: 40, align: "center",
                render: (_, row) => linkedManifestId ? null : (
                  <Button size="small" danger type="text" icon={<DeleteOutlined />}
                    onClick={() => removeItem(row.key)} />
                ),
              },
            ]}
          />

          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>

        {/* ── Action buttons ── */}
        {(() => {
          const editingRow = editingId ? rows.find(r => r.id === editingId) : null;
          const alreadyProcessed = editingRow?.isProcessed ?? false;
          return (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", paddingTop: 8, borderTop: "1px solid var(--app-bg)" }}>
              <SaveActionButton
                saving={saving}
                idleText={editingId ? "Save Changes" : "Save"}
                savingText="Saving..."
                icon={<CheckOutlined />}
                onClick={() => void onSave(false)}
              />
              {/* "Save & Mark Processed" — only show when not already processed */}
              {!alreadyProcessed && (
                <Tooltip placement="bottom" title="Save all changes and mark this arrival as fully verified / processed">
                  <SaveActionButton
                    saving={saving}
                    idleText="Save & Mark Processed"
                    savingText="Saving..."
                    icon={<CheckOutlined />}
                    style={{ background: "#52c41a", borderColor: "#52c41a" }}
                    onClick={() => void onSave(true)}
                  />
                </Tooltip>
              )}
              <Popconfirm
                title="Clear all form data?"
                description="This will reset all fields and the consignment grid."
                okText="Yes, Clear"
                cancelText="Cancel"
                onConfirm={() => {
                  form.resetFields();
                  form.setFieldsValue({ arrivalDate: dayjs(), documentDate: dayjs(), challanDate: dayjs() });
                  setItems([]);
                  setLinkedManifestId(null);
                  setArrivalTransportMode("Road");
                  setEditingId(null);
                }}
              >
                <Button icon={<ClearOutlined />}>Clear</Button>
              </Popconfirm>
              <Button
                icon={<PrinterOutlined />}
                onClick={() => {
                  const row = editingId ? rows.find(r => r.id === editingId) : null;
                  if (row?.isProcessed) {
                    setPrintModalOpen(true);
                  } else {
                    printArrival();
                  }
                }}
              >
                Print
              </Button>
              {editingId !== null && (
                <Popconfirm
                  title="Delete this arrival record?"
                  description="This cannot be undone."
                  onConfirm={() => void onDelete(editingId!)}
                  okText="Yes, Delete"
                  okButtonProps={{ danger: true }}
                >
                  <Button danger icon={<DeleteOutlined />}>Delete</Button>
                </Popconfirm>
              )}
            </div>
          );
        })()}

        <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid var(--app-bg)" }}>
          <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>Documents &amp; Attachments</div>
          <EntityAttachmentsPanel
            entityType="manifest-arrival"
            entityId={editingId ?? 0}
            tempKey={editingId === null ? (createTempKey ?? undefined) : undefined}
            entityLabel={editingId ? rows.find(r => r.id === editingId)?.arrivalNo : "New Arrival"}
            branchId={editingId === null ? formBranchId : undefined}
          />
        </div>

        <BranchCreateModal
          open={branchCreateOpen}
          prefillName={branchPrefill}
          onClose={() => setBranchCreateOpen(false)}
          onCreated={(item) => {
            setBranchSeeds(prev => [item, ...prev.filter(x => x.id !== item.id)]);
            form.setFieldValue("branchId", item.id);
            setBranchCreateOpen(false);
          }}
        />

        {/* ── Print Reports Modal ── */}
        <PrintReportsModal
          open={printModalOpen}
          onClose={() => setPrintModalOpen(false)}
          arrivalNo={editingId ? (rows.find(r => r.id === editingId)?.arrivalNo ?? "") : ""}
          formValues={form.getFieldsValue()}
          items={items}
          branchName={editingId ? (rows.find(r => r.id === editingId)?.branchName ?? "") : ""}
        />
      </Card>
    );
  }

  // ── List view ─────────────────────────────────────────────────────────────
  return (
    <Card className="panel-card">
      <div className="panel-title-row operations-data-only">
        <Space align="center" size={8}>
          <Typography.Title level={4} style={{ margin: 0 }}>Manifest Arrival</Typography.Title>
          <span className="kbd-hint"><kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>A</kbd></span>
        </Space>
        <Space>
          <span className="kbd-hint"><kbd>Alt</kbd>+<kbd>N</kbd></span>
          <Button
            type="primary" icon={<PlusOutlined />}
            className={search.trim() && filteredRows.length === 0 ? "new-btn-highlight" : ""}
            onClick={openNew}
          >
            New Arrival
          </Button>
        </Space>
      </div>

      <Space className="operations-data-only" style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }} wrap>
        <Input
          ref={searchRef}
          className="panel-search-input"
          prefix={<SearchOutlined />}
          placeholder="Search arrival no, challan no, lorry no…  F3"
          allowClear value={search}
          onChange={e => setSearch(e.target.value)}
          onPressEnter={() => void load()}
          style={{ width: 380 }}
        />
        <Space>
          {search.trim() && filteredRows.length === 0 && (
            <Typography.Text type="secondary">No results — press Alt+N to create</Typography.Text>
          )}
          <Button onClick={() => void load()}>
            Refresh <span className="kbd-hint"><kbd>F5</kbd></span>
          </Button>
        </Space>
      </Space>

      <Table
        className="manifest-arrival-list-table"
        rowKey="id"
        loading={loading}
        dataSource={filteredRows}
        size="small"
        tableLayout="fixed"
        scroll={{ x: 1445 }}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        onRow={row => ({ onClick: () => void openEdit(row), style: { cursor: "pointer" } })}
        columns={[
          { title: "#", width: 50, render: (_, __, i) => i + 1 },
          { title: "Arrival No", dataIndex: "arrivalNo", width: 160 },
          {
            title: "Arrival Date", dataIndex: "arrivalDate", width: 110,
            render: (v: string) => dayjs(v).format("DD/MM/YYYY"),
          },
          { title: "Challan No", dataIndex: "challanNo", width: 150 },
          { title: "Branch", dataIndex: "branchName", width: 140, ellipsis: true },
          { title: "From Station", dataIndex: "fromStation", width: 170, ellipsis: true },
          { title: "Lorry No", dataIndex: "lorryNo", width: 120 },
          { title: "Pkgs Desp.", dataIndex: "pkgsDespatched", width: 90, align: "right" },
          { title: "Pkgs Recv.", dataIndex: "pkgsReceived", width: 90, align: "right" },
          {
            title: "Short/Exc", dataIndex: "shortExcess", width: 90, align: "right",
            render: (v: number) => (
              <Tag color={v < 0 ? "red" : v > 0 ? "green" : "default"}>
                {v > 0 ? `+${v}` : v}
              </Tag>
            ),
          },
          { title: "Cnmts", dataIndex: "itemCount", width: 60, align: "right" },
          {
            title: "Status", width: 100, align: "center",
            render: (_, row) => row.isProcessed ? (
              <Tooltip placement="bottom" title={row.processedAt
                ? `Processed ${dayjs(row.processedAt).format("DD/MM/YY HH:mm")}${row.processedBy ? ` by ${row.processedBy}` : ""}`
                : "Processed"}>
                <Tag color="green">Processed</Tag>
              </Tooltip>
            ) : (
              <Tag color="orange">Pending</Tag>
            ),
          },
          {
            title: "", width: 115, align: "center",
            render: (_, row) => (
              <Space size={4} onClick={e => e.stopPropagation()}>
                <Tooltip placement="bottom" title={row.isProcessed ? "Edit this arrival" : "Open to verify goods and mark processed"}>
                  <Button
                    size="small"
                    type={row.isProcessed ? "default" : "primary"}
                    icon={<EditOutlined />}
                    onClick={(e) => { e.stopPropagation(); void openEdit(row); }}
                  >
                    {row.isProcessed ? "Edit" : "Process"}
                  </Button>
                </Tooltip>
                <Popconfirm
                  title="Delete this arrival?"
                  onConfirm={e => { e?.stopPropagation(); void onDelete(row.id); }}
                >
                  <Button
                    size="small" danger icon={<DeleteOutlined />}
                    onClick={e => e.stopPropagation()}
                  />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
    </Card>
  );
};
